import { useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight, BadgeCheck, Crown } from "lucide-react";

/**
 * Tanta fullscreen photo viewer for any user's profile.
 * Tap left/right edges to navigate; tap X to close.
 *
 * Props:
 *  - photos: string[]                              (required)
 *  - name, age, location, verified, vip            (optional header chrome)
 *  - onClose: () => void                           (required)
 */
export default function PhotoViewer({ photos, name, age, location, verified, vip, onClose }) {
  const [idx, setIdx] = useState(0);
  const list = (photos || []).filter(Boolean);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "ArrowRight") setIdx((i) => Math.min(i + 1, list.length - 1));
      else if (e.key === "ArrowLeft") setIdx((i) => Math.max(i - 1, 0));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [list.length, onClose]);

  if (list.length === 0) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center" data-testid="photo-viewer">
      {/* Top progress bars (IG-story style) */}
      <div className="absolute top-3 inset-x-3 flex gap-1 z-10">
        {list.map((_, i) => (
          <div key={i} className="flex-1 h-1 rounded-full bg-white/25 overflow-hidden">
            <div className={`h-full bg-white transition-all ${i < idx ? "w-full" : i === idx ? "w-full" : "w-0"}`} />
          </div>
        ))}
      </div>

      {/* Close */}
      <button onClick={onClose} className="absolute top-7 right-4 z-20 w-10 h-10 rounded-full bg-white/15 backdrop-blur flex items-center justify-center text-white hover:bg-white/25" data-testid="photo-viewer-close" aria-label="Close">
        <X className="w-6 h-6" strokeWidth={3} />
      </button>

      {/* Image */}
      <img src={list[idx]} alt="" className="max-h-[100vh] max-w-full object-contain select-none" draggable={false} />

      {/* Tap zones */}
      <button
        className="absolute left-0 top-0 bottom-0 w-1/3 z-0 cursor-w-resize flex items-center justify-start pl-2 group"
        onClick={() => setIdx((i) => Math.max(i - 1, 0))}
        aria-label="Previous"
        data-testid="photo-viewer-prev"
      >
        {idx > 0 && <ChevronLeft className="w-8 h-8 text-white/40 group-hover:text-white/80" strokeWidth={3} />}
      </button>
      <button
        className="absolute right-0 top-0 bottom-0 w-1/3 z-0 cursor-e-resize flex items-center justify-end pr-2 group"
        onClick={() => setIdx((i) => Math.min(i + 1, list.length - 1))}
        aria-label="Next"
        data-testid="photo-viewer-next"
      >
        {idx < list.length - 1 && <ChevronRight className="w-8 h-8 text-white/40 group-hover:text-white/80" strokeWidth={3} />}
      </button>

      {/* Bottom meta */}
      {(name || age) && (
        <div className="absolute bottom-0 inset-x-0 z-10 px-6 pb-8 pt-20 bg-gradient-to-t from-black/80 to-transparent pointer-events-none">
          <div className="flex items-center gap-2 text-white flex-wrap">
            <h2 className="font-display font-black text-3xl tracking-tight">{name}{age ? `, ${age}` : ""}</h2>
            {verified && <BadgeCheck className="w-6 h-6 text-[#00CED1]" fill="#00CED1" strokeWidth={3} />}
            {vip && <Crown className="w-6 h-6 text-[#00CED1]" fill="#00CED1" strokeWidth={3} />}
          </div>
          {location && <p className="text-white/75 text-sm mt-1">{location}</p>}
        </div>
      )}
    </div>
  );
}
